import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Info, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { PLAN_SCHRIFT } from '../sige-plan/types';
import {
    MAX_BEZEICHNUNG, MAX_PLOT_BREITEN, PLOT_BREITE, STANDARD_SIGE_PLAN_VORLAGEN, schriftPtText,
    type SigePlanVorlagen,
} from '../../services/sigePlanVorlagen';
import {
    editorStart, entwurfUnvollstaendig, fremdeAenderungUebernehmen, vorlagenAusEntwurf, wertAktualisieren, zeileAendern,
    zeileEntfernen, zeileHinzufuegen, zeileVerschieben, zeilenFehler, type EditorZustand, type VorlagenEntwurf,
} from './sigePlanVorlagenEntwurf';

/**
 * Abschnitt „SiGe-Plan: Plotter-Vorlagen" der globalen Einstellungen:
 * Auswahlliste der Plotterbreiten, Standardbreite und Standard-Schriftgröße
 * für die ganze Firma (services/sigePlanVorlagen.ts).
 *
 * Gespeichert wird über den Autosave des Einstellungsdialogs: ein vollständig
 * gültiger Entwurf geht als ganzer Block an `onChange`. Solange eine Zeile
 * unfertig ist, geht nichts hinaus — der letzte gültige Block bleibt wirksam
 * (sigePlanVorlagenEntwurf.ts). Ein neuer `wert` von außen wird in einen
 * unveränderten Entwurf übernommen, sonst angeboten. Kein Browser-Dialog: das
 * Zurücksetzen fragt inline nach.
 */

interface Props {
    /** Eigener Block, sonst der wirksame der Firma — darf sich ändern, während der Abschnitt offen ist. */
    wert: SigePlanVorlagen;
    /** Zusatz zum Hinweis, z. B. wenn gerade der Block eines anderen Admins gilt. */
    hinweis?: string | null;
    onChange: (v: SigePlanVorlagen) => void;
    onZuruecksetzen: () => void;
}

const LABEL = 'block text-[11px] font-bold text-slate-400 uppercase mb-1';
const INPUT = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500';
const PFEIL = 'p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent';

export const SigePlanVorlagenEditor: React.FC<Props> = ({ wert, hinweis, onChange, onZuruecksetzen }) => {
    const [zustand, setZustand] = useState<EditorZustand>(() => editorStart(wert));
    const [letzterWert, setLetzterWert] = useState(wert);
    const [zuruecksetzenFragen, setZuruecksetzenFragen] = useState(false);
    // Neuer Stand von außen (nachgeladener Firmenblock, Block eines anderen
    // Admins nach dem Zurücksetzen): während des Renderns abgleichen — das
    // React-Muster „Zustand an geänderte Props anpassen", ohne Effekt.
    if (wert !== letzterWert) {
        setLetzterWert(wert);
        setZustand(wertAktualisieren(zustand, wert));
    }
    const { entwurf, fremdeAenderung } = zustand;
    const fehler = zeilenFehler(entwurf.zeilen);
    const unvollstaendig = entwurfUnvollstaendig(entwurf);
    const anzahl = entwurf.zeilen.length;

    // Nur ein vollständig gültiger Entwurf geht hinaus; solange eine Zeile
    // unfertig ist, bleibt der zuletzt gesicherte Block wirksam.
    const aendern = (neu: VorlagenEntwurf) => {
        setZustand((z) => ({ ...z, entwurf: neu }));
        const v = vorlagenAusEntwurf(neu);
        if (v) onChange(v);
    };

    const zuruecksetzen = () => {
        setZustand(editorStart(STANDARD_SIGE_PLAN_VORLAGEN));
        setZuruecksetzenFragen(false);
        onZuruecksetzen();
    };

    return (
        <div className="space-y-5 mt-4">
            <div className="flex items-start gap-3 bg-amber-50 p-3 rounded-xl border border-amber-100">
                <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                    Gilt für neue Pläne und für Pläne ohne eigene Einstellung. Bestehende Pläne behalten ihre gespeicherte Breite.
                    {hinweis ? <> {hinweis}</> : null}
                </p>
            </div>

            {fremdeAenderung && (
                <div role="status" className="flex flex-wrap items-center gap-3 bg-sky-50 p-3 rounded-xl border border-sky-200">
                    <p className="text-xs text-sky-800 flex-1 min-w-[12rem]">
                        Die Firmenvorlage wurde inzwischen geändert. „Übernehmen“ zeigt sie und verwirft die Änderung hier.
                    </p>
                    <button
                        type="button"
                        onClick={() => setZustand((z) => fremdeAenderungUebernehmen(z))}
                        className="text-xs font-bold text-white bg-sky-600 hover:bg-sky-700 px-3 py-1.5 rounded-lg"
                    >
                        Übernehmen
                    </button>
                </div>
            )}

            <div>
                <label className={LABEL}>Plotterbreiten (Auswahl im Plankopf)</label>
                <div className="space-y-2">
                    {entwurf.zeilen.map((z, i) => {
                        const f = fehler[i];
                        return (
                            <div key={z.id} className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                                <label className="flex items-center gap-1.5 text-xs text-slate-600 w-24 shrink-0 cursor-pointer" title="Standardbreite neuer Pläne und aller Pläne ohne eigene Breite">
                                    <input
                                        type="radio"
                                        name="sigeplan-standardbreite"
                                        checked={entwurf.standardId === z.id}
                                        onChange={() => aendern({ ...entwurf, standardId: z.id })}
                                        className="accent-amber-600"
                                    />
                                    Standard
                                </label>
                                <div className="relative w-32 shrink-0">
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min={PLOT_BREITE.min}
                                        max={PLOT_BREITE.max}
                                        step={1}
                                        value={z.mm}
                                        onChange={(e) => aendern(zeileAendern(entwurf, z.id, { mm: e.target.value }))}
                                        aria-label={`Breite ${i + 1} in mm`}
                                        aria-invalid={f !== null}
                                        className={`${INPUT} pr-10 ${f ? 'border-red-400 focus:ring-red-400' : ''}`}
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">mm</span>
                                </div>
                                <input
                                    type="text"
                                    value={z.bezeichnung}
                                    maxLength={MAX_BEZEICHNUNG}
                                    placeholder="Bezeichnung (optional), z. B. A3"
                                    onChange={(e) => aendern(zeileAendern(entwurf, z.id, { bezeichnung: e.target.value }))}
                                    aria-label={`Bezeichnung zu Breite ${i + 1}`}
                                    className={`${INPUT} flex-1 min-w-[10rem]`}
                                />
                                <div className="flex items-center gap-1 shrink-0">
                                    <button type="button" onClick={() => aendern(zeileVerschieben(entwurf, z.id, -1))} disabled={i === 0} title="Nach oben" aria-label="Nach oben" className={PFEIL}>
                                        <ArrowUp className="w-4 h-4" />
                                    </button>
                                    <button type="button" onClick={() => aendern(zeileVerschieben(entwurf, z.id, 1))} disabled={i === anzahl - 1} title="Nach unten" aria-label="Nach unten" className={PFEIL}>
                                        <ArrowDown className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => aendern(zeileEntfernen(entwurf, z.id))}
                                        disabled={anzahl <= 1}
                                        title={anzahl <= 1 ? 'Mindestens eine Breite bleibt stehen' : 'Breite entfernen'}
                                        aria-label="Breite entfernen"
                                        className="p-2 rounded-lg border border-red-100 text-red-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:hover:bg-transparent"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                {f && (
                                    <p className="basis-full text-[11px] text-red-600">
                                        {f === 'doppelt'
                                            ? 'Diese Breite steht schon weiter oben — die Zeile zählt nicht.'
                                            : `Nur ganze Millimeter von ${PLOT_BREITE.min} bis ${PLOT_BREITE.max} — so zählt die Zeile nicht.`}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        onClick={() => aendern(zeileHinzufuegen(entwurf))}
                        disabled={anzahl >= MAX_PLOT_BREITEN}
                        className="text-xs font-bold text-amber-700 hover:text-amber-800 flex items-center gap-1 bg-amber-50 px-2.5 py-1.5 rounded-lg border border-amber-100 disabled:opacity-40"
                    >
                        <Plus className="w-3.5 h-3.5" /> Breite hinzufügen
                    </button>
                    <span className="text-[11px] text-slate-400">
                        Höchstens {MAX_PLOT_BREITEN} Breiten, {PLOT_BREITE.min}–{PLOT_BREITE.max} mm. Die Pfeile bestimmen die Reihenfolge in der Auswahl.
                    </span>
                </div>
                {unvollstaendig && (
                    <p role="status" className="text-[11px] font-bold text-red-600 mt-1">
                        Noch nicht gespeichert: Eingabe unvollständig. Bis alle Zeilen stimmen, gilt die zuletzt gespeicherte Vorlage.
                    </p>
                )}
            </div>

            <div>
                <label className={LABEL}>Standard-Schriftgröße der Tabelle</label>
                <div className="flex items-center gap-3">
                    <input
                        type="range"
                        min={PLAN_SCHRIFT.min}
                        max={PLAN_SCHRIFT.max}
                        step={PLAN_SCHRIFT.schritt}
                        value={entwurf.schriftPt}
                        onChange={(e) => aendern({ ...entwurf, schriftPt: Number(e.target.value) })}
                        aria-label="Standard-Schriftgröße des SiGe-Plans"
                        aria-valuetext={schriftPtText(entwurf.schriftPt)}
                        className="flex-1 accent-amber-600"
                    />
                    <span className="tabular-nums text-sm font-bold text-slate-700 w-16 text-right">{schriftPtText(entwurf.schriftPt)}</span>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                    Werkseinstellung {schriftPtText(PLAN_SCHRIFT.vorgabe)}. Alle Schriften des Blatts wachsen mit; größere Schrift macht den Plan länger.
                </p>
            </div>

            <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
                {zuruecksetzenFragen ? (
                    <>
                        <span className="text-xs text-slate-600">Breiten und Schrift auf Werkseinstellung zurücksetzen?</span>
                        <button type="button" onClick={zuruecksetzen} className="text-xs font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg">
                            Ja, zurücksetzen
                        </button>
                        <button type="button" onClick={() => setZuruecksetzenFragen(false)} className="text-xs font-bold text-slate-600 hover:bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">
                            Abbrechen
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        onClick={() => setZuruecksetzenFragen(true)}
                        className="text-[11px] font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider bg-red-50 px-2 py-1 rounded border border-red-100"
                    >
                        <RotateCcw className="w-3 h-3" /> Auf Werkseinstellung zurücksetzen
                    </button>
                )}
            </div>
        </div>
    );
};
